import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  ArrowLeft, Upload, Download, Trash2, Search, AlertTriangle, Stethoscope,
  CheckCircle2, FileSpreadsheet, RefreshCw, BellRing, Clock, PhoneOff,
  Plus, Pencil, X, Wand2, Printer, Mail, CalendarOff, Send,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

/**
 * Admin "Services" view — upload an .xlsx of camper service providers
 * (Speech, OT, etc.), cross-reference against group_schedules, and
 * download a printable conflict report.
 */
export default function ServiceProvidersView({ token, onBack }) {
  const [providers, setProviders] = useState([]);
  const [report, setReport] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tab, setTab] = useState("report"); // "report" | "alerts"
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [showOnlyProtected, setShowOnlyProtected] = useState(false);
  const [editor, setEditor] = useState(null); // null | {mode:"add"|"edit", record}
  const [saving, setSaving] = useState(false);
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [providersList, setProvidersList] = useState([]);
  const [providersListLoading, setProvidersListLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailIntro, setEmailIntro] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Provider Update (day-out + HC text) modal state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updProvider, setUpdProvider] = useState("");
  const [updDate, setUpdDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [updSkipTexts, setUpdSkipTexts] = useState(true);
  const [updSendSms, setUpdSendSms] = useState(true);
  const [updMessage, setUpdMessage] = useState("");
  const [updRecipients, setUpdRecipients] = useState([]);
  const [updSelectedGroups, setUpdSelectedGroups] = useState([]);
  const [updLoadingRecipients, setUpdLoadingRecipients] = useState(false);
  const [updSending, setUpdSending] = useState(false);
  const [dayOuts, setDayOuts] = useState([]);
  const fileRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, rRes, aRes] = await Promise.all([
        axios.get(`${API}/service-providers`, { headers }),
        axios.get(`${API}/service-providers/report`, { headers }),
        axios.get(`${API}/service-providers/alerts/upcoming`, { headers }),
      ]);
      setProviders(pRes.data?.providers || []);
      setReport(rRes.data?.rows || []);
      setAlerts(aRes.data?.alerts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load service providers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onPickFile = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/service-providers/upload`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${res.data.count} session${res.data.count === 1 ? "" : "s"}`);
      if ((res.data.warnings || []).length) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onAutoSchedule = async (overwrite = false) => {
    const promptText = overwrite
      ? "OVERWRITE existing times for everyone except Blake Rothenberg? This re-optimizes the entire schedule."
      : "Auto-schedule everyone with EMPTY times (Blake Rothenberg is excluded)?";
    if (!window.confirm(promptText)) return;
    setAutoScheduling(true);
    setAutoResult(null);
    try {
      const res = await axios.post(
        `${API}/service-providers/auto-schedule`,
        { overwrite },
        { headers }
      );
      setAutoResult(res.data);
      const t = res.data.totals;
      const msg = `Scheduled ${t.scheduled} · Skipped ${t.skipped} · Failed ${t.failed}`;
      if (t.failed > 0) toast.warning(msg);
      else toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-schedule failed");
    } finally {
      setAutoScheduling(false);
    }
  };

  const onClear = async () => {
    // Two-step confirmation — first a generic confirm, then a typed phrase.
    if (!window.confirm("DELETE ALL service-provider records for the current year? This wipes both uploaded AND manual entries and CANNOT be undone.")) return;
    const typed = window.prompt('Type "DELETE ALL" to confirm.', "");
    if (typed !== "DELETE ALL") {
      toast.info("Cancelled — phrase did not match.");
      return;
    }
    try {
      await axios.delete(`${API}/service-providers/clear-all?confirm=YES_DELETE_ALL`, { headers });
      toast.success("Cleared all service-provider records");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear");
    }
  };

  const onDownloadXlsx = async () => {
    try {
      const res = await axios.get(`${API}/service-providers/report/xlsx`, {
        headers, responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `service_providers_report_${new Date().getFullYear()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download xlsx");
    }
  };

  const onDownloadPrintablePdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/service-providers/printable.pdf`, {
        headers, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Service_PullOuts_by_Group_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const onDownloadTutoringRoomXlsx = async () => {
    try {
      const res = await axios.get(`${API}/service-providers/tutoring-room/xlsx`, {
        headers, responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tutoring_room_schedule_${new Date().getFullYear()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Tutoring Room schedule downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download Tutoring Room schedule");
    }
  };

  const openEmailModal = async () => {
    setShowEmailModal(true);
    setSelectedProvider("");
    setEmailTo("");
    setEmailSubject("");
    setEmailIntro("");
    setProvidersListLoading(true);
    try {
      const res = await axios.get(`${API}/service-providers/providers-list`, { headers });
      setProvidersList(res.data?.providers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load providers");
      setProvidersList([]);
    } finally {
      setProvidersListLoading(false);
    }
  };

  const onSelectProvider = (name) => {
    setSelectedProvider(name);
    const p = providersList.find(x => x.provider === name);
    setEmailTo(p?.email || "");
    setEmailSubject(name ? `Camp Service Schedule — ${name}` : "");
  };

  const onSendProviderEmail = async () => {
    if (!selectedProvider) {
      toast.error("Pick a provider first");
      return;
    }
    if (!emailTo.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    setSendingEmail(true);
    try {
      const res = await axios.post(
        `${API}/service-providers/email-provider`,
        {
          provider_name: selectedProvider,
          to_email: emailTo.trim(),
          subject: emailSubject.trim(),
          intro: emailIntro.trim(),
        },
        { headers }
      );
      toast.success(`Sent ${res.data?.rows_sent || 0} rows to ${res.data?.to_email}`);
      setShowEmailModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchDayOuts = async () => {
    try {
      const res = await axios.get(`${API}/service-providers/day-out`, { headers });
      setDayOuts(res.data?.day_outs || []);
    } catch { setDayOuts([]); }
  };

  const openUpdateModal = async () => {
    setShowUpdateModal(true);
    setUpdProvider("");
    setUpdMessage("");
    setUpdRecipients([]);
    setUpdSelectedGroups([]);
    setUpdSkipTexts(true);
    setUpdSendSms(true);
    fetchDayOuts();
    if (!providersList.length) {
      setProvidersListLoading(true);
      try {
        const res = await axios.get(`${API}/service-providers/providers-list`, { headers });
        setProvidersList(res.data?.providers || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load providers");
      } finally {
        setProvidersListLoading(false);
      }
    }
  };

  const fmtDateLabel = (ymd) => {
    const [y, m, d] = (ymd || "").split("-").map(Number);
    if (!y) return ymd;
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "numeric", day: "numeric" });
  };

  const onSelectUpdateProvider = async (name) => {
    setUpdProvider(name);
    setUpdRecipients([]);
    setUpdSelectedGroups([]);
    if (!name) return;
    setUpdMessage(`${name} will NOT be at camp on ${fmtDateLabel(updDate)}. Sessions will be made up — details to follow.`);
    setUpdLoadingRecipients(true);
    try {
      const res = await axios.post(`${API}/service-providers/notify-hcs`, { provider: name }, { headers });
      const recips = res.data?.recipients || [];
      setUpdRecipients(recips);
      setUpdSelectedGroups(recips.filter(r => r.deliverable).map(r => r.group_code));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load head counselors");
    } finally {
      setUpdLoadingRecipients(false);
    }
  };

  const toggleUpdGroup = (gc) => {
    setUpdSelectedGroups(prev => prev.includes(gc) ? prev.filter(g => g !== gc) : [...prev, gc]);
  };

  const onSendProviderUpdate = async () => {
    if (!updProvider) { toast.error("Pick a provider first"); return; }
    if (!updSkipTexts && !updSendSms) { toast.error("Nothing to do — enable at least one action"); return; }
    if (updSendSms && !updMessage.trim()) { toast.error("Enter the message to text"); return; }
    if (updSendSms && updSelectedGroups.length === 0) { toast.error("Select at least one head counselor to text"); return; }
    setUpdSending(true);
    try {
      if (updSkipTexts) {
        await axios.post(`${API}/service-providers/day-out`, {
          provider: updProvider, date: updDate, note: updMessage.trim(),
        }, { headers });
        toast.success(`Reminder texts for ${updProvider} are OFF for ${fmtDateLabel(updDate)}`);
      }
      if (updSendSms) {
        const res = await axios.post(`${API}/service-providers/notify-hcs`, {
          provider: updProvider, message: updMessage.trim(), confirm: true, group_codes: updSelectedGroups,
        }, { headers });
        const { sent = [], failed = [], skipped_no_phone = [] } = res.data || {};
        if (sent.length) toast.success(`Texted ${sent.length} head counselor${sent.length === 1 ? "" : "s"}: ${sent.join(", ")}`);
        if (failed.length) toast.error(`Failed for: ${failed.join(", ")}`);
        if (skipped_no_phone.length) toast.warning(`No phone on file: ${skipped_no_phone.join(", ")}`);
      }
      setShowUpdateModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Provider update failed");
    } finally {
      setUpdSending(false);
    }
  };

  const onDeleteDayOut = async (id) => {
    try {
      await axios.delete(`${API}/service-providers/day-out/${id}`, { headers });
      toast.success("Day-out removed — reminder texts will fire again");
      fetchDayOuts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const onSaveRecord = async (record) => {
    setSaving(true);
    try {
      if (record.id) {
        await axios.put(`${API}/service-providers/${record.id}`, record, { headers });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/service-providers`, record, { headers });
        toast.success("Added");
      }
      setEditor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDeleteRecord = async (record) => {
    // Hard guard: never call DELETE without a real record id, otherwise
    // the URL becomes /service-providers/ and FastAPI used to redirect that
    // to the bulk-clear endpoint. Backend now also rejects bogus ids.
    if (!record || !record.id || !String(record.id).trim()) {
      toast.error("Cannot delete — this row has no id. Please refresh.");
      return;
    }
    if (!window.confirm(`Delete ${record.camper_name}'s ${record.service || "service"} entry?`)) return;
    try {
      await axios.delete(`${API}/service-providers/${encodeURIComponent(record.id)}`, { headers });
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filteredReport = useMemo(() => {
    const lc = q.trim().toLowerCase();
    return report.filter(r => {
      if (showOnlyProtected && !r.any_protected && !r.any_room_conflict) return false;
      if (!lc) return true;
      const conflictText = (r.conflicts_with || [])
        .map(c => `${c.day} ${c.activity}`).join(" ");
      const roomText = (r.room_conflicts || [])
        .map(rc => `${rc.day} ${rc.room} ${rc.other_camper}`).join(" ");
      const blob = [
        r.camper_name, r.group_code, r.service, r.provider,
        r.days_raw, r.times_raw, r.location, conflictText, roomText,
      ].join(" ").toLowerCase();
      return blob.includes(lc);
    });
  }, [report, q, showOnlyProtected]);

  const protectedCount = report.filter(r => r.any_protected).length;
  const roomConflictCount = report.filter(r => r.any_room_conflict).length;
  const camperCount = new Set(report.map(r => r.camper_name)).size;

  return (
    <div className="space-y-4" data-testid="service-providers-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="services-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 text-[#1E3A8A]">
              <Stethoscope className="h-5 w-5 text-[#D4A017]" />
              Services
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload provider schedules &amp; auto-detect activity conflicts
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={onUpload}
            data-testid="services-file-input"
          />
          <Button
            onClick={onPickFile}
            disabled={uploading}
            className="bg-[#1E3A8A] hover:bg-[#16306d] text-white"
            data-testid="services-upload-btn"
          >
            <Upload className="h-4 w-4 mr-1" />
            {uploading ? "Uploading…" : "Upload .xlsx"}
          </Button>
          <Button
            onClick={() => setEditor({ mode: "add", record: {} })}
            className="bg-[#D4A017] hover:bg-[#B88912] text-white"
            data-testid="services-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Camper
          </Button>
          <Button
            onClick={() => onAutoSchedule(false)}
            disabled={autoScheduling || !providers.length}
            className="bg-[#10B981] hover:bg-[#059669] text-white"
            data-testid="services-auto-schedule-btn"
            title="Auto-generate non-conflicting times for everyone except Blake Rothenberg"
          >
            <Wand2 className="h-4 w-4 mr-1" />
            {autoScheduling ? "Scheduling…" : "Auto-Schedule"}
          </Button>
          {!!providers.length && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAutoSchedule(true)}
              disabled={autoScheduling}
              className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
              data-testid="services-auto-schedule-overwrite-btn"
              title="Re-optimize the schedule from scratch (overwrites existing times)"
            >
              Re-optimize
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onDownloadXlsx}
            disabled={!report.length}
            className="border-[#D4A017] text-[#1E3A8A] hover:bg-[#D4A017]/10"
            data-testid="services-download-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Download Report
          </Button>
          <Button
            variant="outline"
            onClick={onDownloadPrintablePdf}
            disabled={!providers.length || downloadingPdf}
            className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A]/10"
            data-testid="services-print-group-pdf-btn"
            title="Download a real PDF, one group per page — hand each section to its head counselor"
          >
            <Printer className="h-4 w-4 mr-1" />
            {downloadingPdf ? "Building…" : "Print Group PDF"}
          </Button>
          <Button
            variant="outline"
            onClick={onDownloadTutoringRoomXlsx}
            disabled={!providers.length}
            className="border-amber-500 text-amber-700 hover:bg-amber-50"
            data-testid="services-tutoring-room-xlsx-btn"
            title="Download an XLSX of just the days & times the Tutoring Room is booked"
          >
            <Download className="h-4 w-4 mr-1" /> Tutoring Room Schedule
          </Button>
          <Button
            variant="outline"
            onClick={openEmailModal}
            disabled={!providers.length}
            className="border-[#D4A017] text-[#1E3A8A] hover:bg-[#D4A017]/10"
            data-testid="services-email-provider-btn"
            title="Email a service provider their own kids' schedule"
          >
            <Mail className="h-4 w-4 mr-1" /> Email Provider
          </Button>
          <Button
            variant="outline"
            onClick={openUpdateModal}
            disabled={!providers.length}
            className="border-rose-400 text-rose-700 hover:bg-rose-50"
            data-testid="services-provider-update-btn"
            title="Provider out or day change — pause that day's reminder texts and text the affected head counselors"
          >
            <CalendarOff className="h-4 w-4 mr-1" /> Provider Update
          </Button>
          <Button variant="outline" size="icon" onClick={load} disabled={loading} data-testid="services-refresh-btn" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {!!providers.length && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="services-clear-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Campers" value={camperCount} accent="navy" testId="stat-campers" />
        <StatCard label="Sessions" value={report.length} accent="gold" testId="stat-sessions" />
        <StatCard label="Protected Conflicts" value={protectedCount} accent="red" testId="stat-conflicts" />
        <StatCard label="Room Clashes" value={roomConflictCount} accent="amber" testId="stat-room-clashes" />
        <StatCard label="Uploaded Rows" value={providers.length} accent="muted" testId="stat-uploaded" />
      </div>

      {/* Auto-Schedule result panel */}
      {autoResult && (
        <Card className="border-2 border-[#10B981]/40 bg-emerald-50/40" data-testid="auto-schedule-result">
          <CardHeader className="py-2 px-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-bold text-[#10B981] flex items-center gap-2">
              <Wand2 className="h-4 w-4" />
              Auto-Schedule Result
              <span className="text-xs font-normal text-gray-600 ml-2">
                {autoResult.totals.scheduled} scheduled · {autoResult.totals.skipped} skipped · {autoResult.totals.failed} need attention
              </span>
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setAutoResult(null)} className="h-7 w-7" data-testid="auto-schedule-result-close">
              <X className="h-3.5 w-3.5" />
            </Button>
          </CardHeader>
          <CardContent className="py-2 px-3 space-y-2">
            {autoResult.failed?.length > 0 && (
              <div className="text-xs">
                <div className="font-bold text-red-700 mb-1">
                  ⚠ Could not auto-schedule ({autoResult.failed.length}):
                </div>
                <div className="space-y-0.5 max-h-32 overflow-y-auto pl-2">
                  {autoResult.failed.map((f, i) => (
                    <div key={i} className="text-red-700">
                      <span className="font-semibold">{f.camper_name}</span>
                      <span className="text-gray-600"> — {f.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {autoResult.scheduled?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer font-bold text-[#10B981] hover:underline">
                  ✓ Scheduled ({autoResult.scheduled.length}) — click to expand
                </summary>
                <div className="space-y-0.5 max-h-48 overflow-y-auto pl-2 mt-1">
                  {autoResult.scheduled.map((s, i) => (
                    <div key={i} className="border-b border-emerald-100 pb-0.5">
                      <span className="font-semibold">{s.camper_name}</span>
                      <span className="text-gray-500"> ({s.group_code})</span>
                      <span className="text-gray-700"> · {s.service} </span>
                      <span className="text-emerald-700 font-mono ml-1">{s.picks.map(p => `${p.day.slice(0,3)} ${p.time}`).join(" + ")}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            {autoResult.skipped?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer font-semibold text-gray-600 hover:underline">
                  Skipped ({autoResult.skipped.length})
                </summary>
                <div className="space-y-0.5 max-h-32 overflow-y-auto pl-2 mt-1">
                  {autoResult.skipped.map((s, i) => (
                    <div key={i} className="text-gray-600">
                      <span className="font-semibold">{s.camper_name}</span>
                      <span> — {s.reason}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!providers.length && !loading && (
        <Card className="border-dashed border-2 border-[#1E3A8A]/30">
          <CardContent className="py-12 text-center">
            <FileSpreadsheet className="h-12 w-12 mx-auto text-[#1E3A8A]/40 mb-3" />
            <h3 className="font-heading font-bold text-[#1E3A8A] text-base">No services uploaded yet</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
              Upload an .xlsx with columns: <span className="font-mono text-xs">Camper Name, Group, Service, Provider, Days, Times</span>.
              Multi-line time cells and blank-name continuations are supported.
            </p>
            <p className="text-xs text-[#D4A017] mt-3 max-w-md mx-auto flex items-center justify-center gap-1.5">
              <BellRing className="h-3.5 w-3.5" />
              Head counselors will be auto-texted <span className="font-bold">10 min before each session</span> every week.
            </p>
            <Button onClick={onPickFile} className="mt-4 bg-[#1E3A8A] hover:bg-[#16306d] text-white" data-testid="services-empty-upload">
              <Upload className="h-4 w-4 mr-1" /> Upload .xlsx
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      {!!providers.length && (
        <div className="flex items-center gap-2 border-b border-gray-200 pb-0">
          <button
            onClick={() => setTab("report")}
            data-testid="services-tab-report"
            className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === "report"
                ? "border-[#D4A017] text-[#1E3A8A]"
                : "border-transparent text-gray-500 hover:text-[#1E3A8A]"
            }`}
          >
            <FileSpreadsheet className="h-4 w-4 inline mr-1.5" />
            Conflict Report
          </button>
          <button
            onClick={() => setTab("alerts")}
            data-testid="services-tab-alerts"
            className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === "alerts"
                ? "border-[#D4A017] text-[#1E3A8A]"
                : "border-transparent text-gray-500 hover:text-[#1E3A8A]"
            }`}
          >
            <BellRing className="h-4 w-4 inline mr-1.5" />
            HC Alerts ({alerts.length})
          </button>
        </div>
      )}

      {/* Alerts panel */}
      {!!providers.length && tab === "alerts" && (
        <AlertsPanel alerts={alerts} q={q} setQ={setQ} />
      )}

      {/* Report table */}
      {!!providers.length && tab === "report" && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base text-[#1E3A8A]">Conflict Report</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Search camper / group / activity"
                    className="pl-8 h-9 w-64"
                    data-testid="services-search"
                  />
                </div>
                <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showOnlyProtected}
                    onChange={e => setShowOnlyProtected(e.target.checked)}
                    data-testid="services-protected-toggle"
                  />
                  Conflicts only
                </label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full text-xs min-w-[1800px]" data-testid="services-report-table">
                <thead className="sticky top-0 bg-[#1E3A8A] text-white z-10">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold">Division</th>
                    <th className="px-2 py-2 text-left font-semibold">Camper Name</th>
                    <th className="px-2 py-2 text-left font-semibold">Group</th>
                    <th className="px-2 py-2 text-left font-semibold">Service</th>
                    <th className="px-2 py-2 text-left font-semibold">Provider</th>
                    <th className="px-2 py-2 text-left font-semibold">Freq &amp; Dur</th>
                    <th className="px-2 py-2 text-left font-semibold">Agency</th>
                    <th className="px-2 py-2 text-left font-semibold">Start</th>
                    <th className="px-2 py-2 text-left font-semibold">Days</th>
                    <th className="px-2 py-2 text-left font-semibold">Time</th>
                    <th className="px-2 py-2 text-left font-semibold">Location</th>
                    <th className="px-2 py-2 text-left font-semibold">Phone</th>
                    <th className="px-2 py-2 text-left font-semibold">Email</th>
                    <th className="px-2 py-2 text-left font-semibold">Paperwork</th>
                    <th className="px-2 py-2 text-left font-semibold">Conflicts</th>
                    <th className="px-2 py-2 text-left font-semibold">Recommended Pull Times</th>
                    <th className="px-2 py-2 text-center font-semibold sticky right-0 bg-[#1E3A8A]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReport.map((r, i) => {
                    const conflicts = r.conflicts_with || [];
                    const roomClashes = r.room_conflicts || [];
                    const isProt = !!r.any_protected;
                    const isRoom = !!r.any_room_conflict;
                    const rowBg = isProt
                      ? "bg-red-50 border-b border-red-100 align-top"
                      : isRoom
                      ? "bg-amber-50 border-b border-amber-100 align-top"
                      : (i % 2 ? "bg-gray-50" : "bg-white") + " border-b align-top";
                    return (
                      <tr
                        key={i}
                        className={rowBg}
                        data-testid={`services-row-${i}`}
                      >
                        <td className="px-2 py-1.5">{r.division}</td>
                        <td className="px-2 py-1.5 font-medium text-[#1E3A8A]">
                          {r.camper_name}
                          {!r.camper_found && (
                            <div className="text-[10px] text-amber-700 font-normal">
                              <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                              Not in roster
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 font-mono">{r.group_code || "—"}</td>
                        <td className="px-2 py-1.5">{r.service}</td>
                        <td className="px-2 py-1.5">{r.provider}</td>
                        <td className="px-2 py-1.5">{r.frequency}</td>
                        <td className="px-2 py-1.5">{r.agency}</td>
                        <td className="px-2 py-1.5">{r.start_date}</td>
                        <td className="px-2 py-1.5 whitespace-pre-line">{r.days_raw}</td>
                        <td className="px-2 py-1.5 whitespace-pre-line font-mono">{r.times_raw}</td>
                        <td className="px-2 py-1.5">{r.location}</td>
                        <td className="px-2 py-1.5 font-mono">{r.phone}</td>
                        <td className="px-2 py-1.5 break-all">{r.email}</td>
                        <td className="px-2 py-1.5">{r.notes}</td>
                        <td className="px-2 py-1.5">
                          {conflicts.length === 0 && roomClashes.length === 0 ? (
                            <span className="text-gray-400">— no schedule on file</span>
                          ) : (
                            <div className="space-y-0.5">
                              {conflicts.map((c, j) => (
                                <div
                                  key={`a-${j}`}
                                  className={`text-[11px] ${c.protected ? "font-bold text-red-700" : ""}`}
                                >
                                  {c.protected && (
                                    <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                                  )}
                                  <span className="text-gray-600">
                                    {(c.day || "").slice(0, 3)} {c.time}
                                  </span>
                                  {" → "}
                                  {c.activity ? (
                                    <span>{c.activity}</span>
                                  ) : (
                                    <span className="italic text-gray-400">(free period)</span>
                                  )}
                                </div>
                              ))}
                              {roomClashes.map((rc, j) => (
                                <div
                                  key={`r-${j}`}
                                  className="text-[11px] font-semibold text-amber-700"
                                  data-testid={`room-clash-${i}-${j}`}
                                  title={`${rc.room} double-booked`}
                                >
                                  <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                                  <span className="text-amber-800">
                                    {(rc.day || "").slice(0, 3)} {rc.my_time}
                                  </span>
                                  {" — "}
                                  <span className="font-bold">{rc.room}</span>
                                  {" also has "}
                                  <span className="font-bold">{rc.other_camper}</span>
                                  {rc.other_group ? ` (${rc.other_group})` : ""}
                                  {" "}<span className="text-amber-700">{rc.other_time}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {(() => {
                            const recRows = conflicts
                              .filter(c => c.protected)
                              .map(c => ({
                                day: (c.day || "").slice(0, 3),
                                recs: c.recommendations || [],
                              }));
                            if (recRows.length === 0) {
                              return <span className="text-gray-400">—</span>;
                            }
                            return (
                              <div className="space-y-1">
                                {recRows.map((rr, k) => (
                                  <div key={k} className="text-[11px]">
                                    <span className="font-semibold text-[#1E3A8A]">{rr.day}:</span>{" "}
                                    {rr.recs.length === 0 ? (
                                      <span className="italic text-gray-500">no clean block</span>
                                    ) : (
                                      <span>
                                        {rr.recs.map((a, m) => (
                                          <span key={m}>
                                            {m > 0 && ", "}
                                            <span className="text-[#D4A017] font-mono">{a.time}</span>{" "}
                                            <span>{a.activity}</span>
                                          </span>
                                        ))}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-2 py-1.5 text-center sticky right-0 bg-inherit">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setEditor({ mode: "edit", record: r })}
                              title="Edit"
                              data-testid={`services-edit-${i}`}
                            >
                              <Pencil className="h-3.5 w-3.5 text-[#1E3A8A]" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 hover:bg-red-50"
                              onClick={() => onDeleteRecord(r)}
                              title="Delete"
                              data-testid={`services-delete-${i}`}
                            >
                              <X className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredReport.length && (
                    <tr><td colSpan={17} className="px-2 py-8 text-center text-gray-400">No rows match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add / Edit dialog */}
      {editor && (
        <RecordEditor
          mode={editor.mode}
          initial={editor.record}
          saving={saving}
          onCancel={() => setEditor(null)}
          onSave={onSaveRecord}
        />
      )}

      {/* Email Provider modal */}
      <Dialog open={showEmailModal} onOpenChange={(o) => !o && setShowEmailModal(false)}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="email-provider-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-[#1E3A8A] flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#D4A017]" />
              Email Service Provider
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Provider</Label>
              {providersListLoading ? (
                <div className="text-sm text-gray-500 italic mt-1">Loading providers…</div>
              ) : providersList.length === 0 ? (
                <div className="text-sm text-gray-500 italic mt-1">
                  No providers found in the current year.
                </div>
              ) : (
                <select
                  value={selectedProvider}
                  onChange={(e) => onSelectProvider(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
                  data-testid="email-provider-select"
                >
                  <option value="">— Pick a provider —</option>
                  {providersList.map((p) => (
                    <option key={p.provider} value={p.provider}>
                      {p.provider} ({p.camper_count} {p.camper_count === 1 ? "camper" : "campers"})
                      {p.email ? "" : " · no email on file"}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {selectedProvider && (() => {
              const p = providersList.find(x => x.provider === selectedProvider);
              if (!p) return null;
              return (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs font-semibold text-gray-700 mb-1">
                    Campers on this email ({p.campers.length})
                  </div>
                  <div className="text-xs text-gray-600 flex flex-wrap gap-1.5">
                    {p.campers.map((c) => (
                      <span
                        key={c}
                        className="inline-block bg-white border border-gray-200 px-2 py-0.5 rounded"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  {p.agency && (
                    <div className="text-xs text-gray-500 mt-2">Agency: {p.agency}</div>
                  )}
                </div>
              );
            })()}

            <div>
              <Label className="text-xs font-semibold text-gray-700">Send To</Label>
              <Input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="provider@example.com"
                className="mt-1"
                data-testid="email-provider-to-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Defaults to the email on file for the selected provider.
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700">Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Camp Service Schedule — …"
                className="mt-1"
                data-testid="email-provider-subject-input"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700">Intro Message (optional)</Label>
              <Textarea
                value={emailIntro}
                onChange={(e) => setEmailIntro(e.target.value)}
                rows={3}
                placeholder="Quick note that will appear above the schedule table…"
                className="mt-1"
                data-testid="email-provider-intro-input"
              />
            </div>
          </div>

          <DialogFooter className="border-t pt-3">
            <Button
              variant="outline"
              onClick={() => setShowEmailModal(false)}
              data-testid="email-provider-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={onSendProviderEmail}
              disabled={!selectedProvider || !emailTo.trim() || sendingEmail}
              className="bg-[#1E3A8A] hover:bg-[#16306d] text-white"
              data-testid="email-provider-send-btn"
            >
              <Mail className="h-4 w-4 mr-1" />
              {sendingEmail ? "Sending…" : "Send Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider Update modal — day-out + text head counselors */}
      <Dialog open={showUpdateModal} onOpenChange={(o) => !o && setShowUpdateModal(false)}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="provider-update-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-[#1E3A8A] flex items-center gap-2">
              <CalendarOff className="h-5 w-5 text-rose-600" />
              Provider Update — Out / Day Change
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-700">Provider</Label>
                {providersListLoading ? (
                  <div className="text-sm text-gray-500 italic mt-1">Loading providers…</div>
                ) : (
                  <select
                    value={updProvider}
                    onChange={(e) => onSelectUpdateProvider(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
                    data-testid="provider-update-select"
                  >
                    <option value="">— Pick a provider —</option>
                    {providersList.map((p) => (
                      <option key={p.provider} value={p.provider}>
                        {p.provider} ({p.camper_count} {p.camper_count === 1 ? "camper" : "campers"})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700">Date they're out</Label>
                <Input
                  type="date"
                  value={updDate}
                  onChange={(e) => setUpdDate(e.target.value)}
                  className="mt-1"
                  data-testid="provider-update-date"
                />
              </div>
            </div>

            <label className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50/60 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={updSkipTexts}
                onChange={(e) => setUpdSkipTexts(e.target.checked)}
                className="mt-0.5 accent-rose-600"
                data-testid="provider-update-skip-texts"
              />
              <span className="text-xs text-gray-700">
                <span className="font-bold text-rose-700">Turn OFF the automatic reminder texts</span> for this provider on {fmtDateLabel(updDate)}.
                Head counselors will NOT get the usual 10-min-before pull-out texts for {updProvider || "this provider"} that day.
              </span>
            </label>

            <label className="flex items-start gap-2 rounded-md border border-[#1E3A8A]/20 bg-blue-50/50 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={updSendSms}
                onChange={(e) => setUpdSendSms(e.target.checked)}
                className="mt-0.5 accent-[#1E3A8A]"
                data-testid="provider-update-send-sms"
              />
              <span className="text-xs text-gray-700">
                <span className="font-bold text-[#1E3A8A]">Text the affected head counselors now</span> with the update below.
              </span>
            </label>

            {updSendSms && (
              <>
                <div>
                  <Label className="text-xs font-semibold text-gray-700">Message</Label>
                  <Textarea
                    value={updMessage}
                    onChange={(e) => setUpdMessage(e.target.value)}
                    rows={3}
                    placeholder="e.g. McKayla is not coming tomorrow but will be here Friday to make up the sessions."
                    className="mt-1"
                    data-testid="provider-update-message"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Sent as: <span className="font-mono">[Camper Snapshot — GROUP] Service update: …</span>
                  </p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-700">
                    Who gets the text {updRecipients.length > 0 && `(${updSelectedGroups.length} of ${updRecipients.length} selected)`}
                  </Label>
                  {updLoadingRecipients && <div className="text-sm text-gray-500 italic mt-1">Finding affected head counselors…</div>}
                  {!updLoadingRecipients && !updProvider && (
                    <div className="text-sm text-gray-400 italic mt-1">Pick a provider to see their groups' head counselors.</div>
                  )}
                  {!updLoadingRecipients && updRecipients.length > 0 && (
                    <div className="mt-1 space-y-1.5 max-h-52 overflow-y-auto pr-1" data-testid="provider-update-recipients">
                      {updRecipients.map((r) => (
                        <label
                          key={r.group_code}
                          className={`flex items-start gap-2 rounded-md border p-2 ${r.deliverable ? "cursor-pointer bg-white border-gray-200 hover:border-[#1E3A8A]/40" : "bg-gray-50 border-gray-200 opacity-60"}`}
                          data-testid={`provider-update-recipient-${r.group_code}`}
                        >
                          <input
                            type="checkbox"
                            disabled={!r.deliverable}
                            checked={updSelectedGroups.includes(r.group_code)}
                            onChange={() => toggleUpdGroup(r.group_code)}
                            className="mt-0.5 accent-[#1E3A8A]"
                          />
                          <span className="text-xs">
                            <span className="font-bold text-[#1E3A8A]">{r.group_code}</span>
                            {r.head_counselor && <span className="text-gray-700"> — {r.head_counselor}</span>}
                            {!r.deliverable && <span className="text-rose-600 font-semibold"> (no phone on file)</span>}
                            <span className="block text-gray-500">{r.campers.join(", ")}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {dayOuts.length > 0 && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs font-semibold text-gray-700 mb-1.5">Scheduled out-days (reminder texts paused)</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {dayOuts.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 text-xs bg-white border border-gray-200 rounded px-2 py-1" data-testid={`provider-dayout-${d.id}`}>
                      <span><span className="font-bold">{d.provider}</span> — {fmtDateLabel(d.date)}</span>
                      <button
                        onClick={() => onDeleteDayOut(d.id)}
                        className="text-gray-400 hover:text-red-600"
                        title="Remove — reminder texts fire again that day"
                        data-testid={`provider-dayout-delete-${d.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => setShowUpdateModal(false)} data-testid="provider-update-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={onSendProviderUpdate}
              disabled={!updProvider || updSending}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="provider-update-submit-btn"
            >
              <Send className="h-4 w-4 mr-1" />
              {updSending ? "Working…" : updSendSms && updSkipTexts ? "Pause Texts + Send Update" : updSendSms ? "Send Update Text" : "Pause Texts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Per-day session helpers ─────────────────────────────────────────────
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const SHORT_DAY = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri" };
const DAY_PREFIX_LOOKUP = {
  monday: "Monday", mon: "Monday", m: "Monday",
  tuesday: "Tuesday", tue: "Tuesday", tues: "Tuesday", tu: "Tuesday",
  wednesday: "Wednesday", wed: "Wednesday", weds: "Wednesday", w: "Wednesday",
  thursday: "Thursday", thur: "Thursday", thurs: "Thursday",
  thu: "Thursday", th: "Thursday", r: "Thursday",
  friday: "Friday", fri: "Friday", f: "Friday",
};

// Parse a freeform "10:20 AM - 10:50 AM" / "10:20-10:50" string into {start, end}
function parseTimeRange(str) {
  if (!str) return null;
  const m = String(str).match(/(\d{1,2}:?\d{0,2}\s*[AaPp]?\.?[Mm]?\.?)\s*[-–—to]+\s*(\d{1,2}:?\d{0,2}\s*[AaPp]?\.?[Mm]?\.?)/);
  if (!m) return null;
  return { start: m[1].trim(), end: m[2].trim() };
}

// Build the per-day grid {Monday: [{start,end}], ...} from a record's
// days_raw + times_raw. Mirrors backend _expand_sessions logic.
function parseRecordToDayGrid(daysRaw, timesRaw) {
  const grid = { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [] };
  if (!timesRaw) return grid;
  // Parse days_raw → list of days
  const dRaw = (daysRaw || "").toLowerCase();
  const targetDays = [];
  for (const wd of WEEKDAYS) {
    const re = new RegExp(`\\b${SHORT_DAY[wd].toLowerCase()}|\\b${wd.toLowerCase()}`);
    if (re.test(dRaw)) targetDays.push(wd);
  }
  // Parse each line of times_raw
  const lines = timesRaw.split(/[\n;]+/).map(l => l.trim()).filter(Boolean);
  const perDay = {}; // day → ranges
  const broadcast = [];
  for (const line of lines) {
    const m = line.match(/^\s*([A-Za-z]+)\s+(.*)$/);
    let lineDay = null;
    let timePart = line;
    if (m) {
      const prefix = m[1].toLowerCase();
      if (DAY_PREFIX_LOOKUP[prefix]) {
        lineDay = DAY_PREFIX_LOOKUP[prefix];
        timePart = m[2].trim();
      }
    }
    const rng = parseTimeRange(timePart);
    if (!rng) continue;
    if (lineDay) (perDay[lineDay] = perDay[lineDay] || []).push(rng);
    else broadcast.push(rng);
  }
  // Apply per-day prefixed lines
  for (const [d, ranges] of Object.entries(perDay)) {
    grid[d] = ranges.slice();
  }
  // Apply broadcast lines to days in days_raw that DON'T have explicit lines
  const daysForBroadcast = targetDays.length ? targetDays : Object.keys(perDay);
  for (const d of daysForBroadcast) {
    if (perDay[d]) continue;
    for (const r of broadcast) grid[d].push({ ...r });
  }
  return grid;
}

// Build days_raw + times_raw from the per-day grid. If every active day has
// the same set of times → unprefixed single block. Otherwise → day-prefixed
// lines.
function buildRawFromDayGrid(grid) {
  const activeDays = WEEKDAYS.filter(d => (grid[d] || []).length > 0);
  if (activeDays.length === 0) return { days_raw: "", times_raw: "" };
  const sig = (arr) => arr.map(t => `${t.start}|${t.end}`).slice().sort().join("__");
  const sigs = new Set(activeDays.map(d => sig(grid[d])));
  let times_raw;
  if (sigs.size === 1) {
    // All active days share the same time set → unprefixed
    times_raw = grid[activeDays[0]].map(t => `${t.start} - ${t.end}`).join("\n");
  } else {
    // Per-day-different → day-prefixed lines
    const lines = [];
    for (const d of activeDays) {
      for (const t of grid[d]) lines.push(`${SHORT_DAY[d]} ${t.start} - ${t.end}`);
    }
    times_raw = lines.join("\n");
  }
  return { days_raw: activeDays.join(", "), times_raw };
}


function RecordEditor({ mode, initial, saving, onCancel, onSave }) {
  const [r, setR] = useState({
    division: initial.division || "",
    camper_name: initial.camper_name || "",
    group_code: initial.group_code || "",
    service: initial.service || "",
    provider: initial.provider || "",
    frequency: initial.frequency || "",
    agency: initial.agency || "",
    start_date: initial.start_date || "",
    location: initial.location || "",
    phone: initial.phone || "",
    email: initial.email || "",
    notes: initial.notes || "",
  });
  const [dayGrid, setDayGrid] = useState(() =>
    parseRecordToDayGrid(initial.days_raw || "", initial.times_raw || "")
  );
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const set = (k) => (e) => setR({ ...r, [k]: e.target.value });

  const addTime = (day) => {
    setDayGrid({
      ...dayGrid,
      [day]: [...(dayGrid[day] || []), { start: "", end: "" }],
    });
  };
  const updateTime = (day, idx, field, value) => {
    const next = { ...dayGrid };
    next[day] = (next[day] || []).map((t, i) => i === idx ? { ...t, [field]: value } : t);
    setDayGrid(next);
  };
  const removeTime = (day, idx) => {
    const next = { ...dayGrid };
    next[day] = (next[day] || []).filter((_, i) => i !== idx);
    setDayGrid(next);
  };

  const submit = () => {
    if (!r.camper_name.trim()) {
      toast.error("Camper Name is required");
      return;
    }
    // Validate every active session has both start and end
    for (const d of WEEKDAYS) {
      for (const [i, t] of (dayGrid[d] || []).entries()) {
        if (!t.start.trim() || !t.end.trim()) {
          toast.error(`${d} row ${i + 1} — fill in both start and end time, or remove the row`);
          return;
        }
      }
    }
    const { days_raw, times_raw } = buildRawFromDayGrid(dayGrid);
    onSave({ ...r, days_raw, times_raw, id: initial.id || undefined });
  };

  // Extract duration in minutes from frequency string ("2x30", "30 min", "1x45 mins")
  const inferDurationMin = () => {
    const m = (r.frequency || "").match(/(\d{2,3})\s*(?:m|min)?/i);
    if (m) {
      const v = parseInt(m[1], 10);
      if (v >= 10 && v <= 120) return v;
    }
    return 30;
  };

  // Active days = days the user has added times to in the grid
  const activeDays = () => WEEKDAYS.filter(d => (dayGrid[d] || []).length > 0);

  const onSuggest = async () => {
    if (!r.camper_name.trim()) {
      toast.error("Enter the camper name first");
      return;
    }
    setSuggesting(true);
    setSuggestions(null);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/service-providers/suggest-times`, {
        camper_name: r.camper_name,
        days: activeDays(),
        duration_min: inferDurationMin(),
        location: r.location,
        exclude_record_id: initial.id || "",
        top_n: 8,
      }, { headers });
      setSuggestions(res.data);
      if (!res.data.suggestions || res.data.suggestions.length === 0) {
        toast.info(res.data.messages?.[0] || "No clean windows found");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not get suggestions");
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = (s) => {
    // s = {day, start_time, end_time, times_raw, ...}
    const day = s.day;
    if (!WEEKDAYS.includes(day)) return;
    const newSession = { start: s.start_time, end: s.end_time };
    // Don't double-add the same slot
    const already = (dayGrid[day] || []).some(t => t.start === newSession.start && t.end === newSession.end);
    if (already) {
      toast.info(`${day} ${s.times_raw} is already in the schedule`);
      return;
    }
    setDayGrid({ ...dayGrid, [day]: [...(dayGrid[day] || []), newSession] });
    toast.success(`Added ${SHORT_DAY[day]} ${s.times_raw}`);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1E3A8A]">
            {mode === "add" ? "Add Camper" : `Edit ${initial.camper_name || ""}`}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto py-1 pr-1 flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Camper Name *" value={r.camper_name} onChange={set("camper_name")} testId="ed-camper-name" />
            <Field label="Camp Group" value={r.group_code} onChange={set("group_code")} placeholder="e.g. N7 or N07" testId="ed-group-code" />
            <Field label="Division" value={r.division} onChange={set("division")} placeholder="Skipper / Mariner" testId="ed-division" />
            <Field label="Service" value={r.service} onChange={set("service")} placeholder="Speech / OT / Tutor" testId="ed-service" />
            <Field label="Service Provider" value={r.provider} onChange={set("provider")} testId="ed-provider" />
            <Field label="Frequency & Duration" value={r.frequency} onChange={set("frequency")} placeholder="2x30" testId="ed-frequency" />
            <Field label="Agency" value={r.agency} onChange={set("agency")} testId="ed-agency" />
            <Field label="Start Date" value={r.start_date} onChange={set("start_date")} placeholder="July 6" testId="ed-start" />
            <Field label="Location" value={r.location} onChange={set("location")} testId="ed-location" />
            <Field label="Phone Number" value={r.phone} onChange={set("phone")} testId="ed-phone" />
            <Field label="Email" value={r.email} onChange={set("email")} testId="ed-email" />
            <Field label="Paperwork / Notes" value={r.notes} onChange={set("notes")} testId="ed-notes" />
          </div>

          {/* ── Per-day schedule editor ─────────────────────────────── */}
          <div className="mt-4 border-2 border-[#1E3A8A]/20 rounded-lg p-3 bg-[#F8FAFC]" data-testid="ed-schedule-editor">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-[#1E3A8A]">Schedule</div>
              <div className="text-[11px] text-gray-500">
                Click <span className="font-mono bg-white border px-1 rounded">+ time</span> on a day to add a session. Empty days are ignored.
              </div>
            </div>
            <div className="space-y-1.5">
              {WEEKDAYS.map((day) => {
                const slots = dayGrid[day] || [];
                const active = slots.length > 0;
                return (
                  <div
                    key={day}
                    className={`flex items-start gap-2 p-2 rounded-md border ${active ? "bg-white border-[#1E3A8A]/30" : "bg-gray-50/60 border-gray-200"}`}
                    data-testid={`ed-day-row-${day}`}
                  >
                    <div className={`w-20 pt-1.5 text-sm font-semibold ${active ? "text-[#1E3A8A]" : "text-gray-400"}`}>
                      {day}
                    </div>
                    <div className="flex-1 space-y-1">
                      {slots.length === 0 && (
                        <div className="text-xs text-gray-400 italic pt-1.5">No sessions this day</div>
                      )}
                      {slots.map((t, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <Input
                            value={t.start}
                            onChange={(e) => updateTime(day, i, "start", e.target.value)}
                            placeholder="10:00 AM"
                            className="h-8 w-28 text-xs"
                            data-testid={`ed-time-start-${day}-${i}`}
                          />
                          <span className="text-gray-400">–</span>
                          <Input
                            value={t.end}
                            onChange={(e) => updateTime(day, i, "end", e.target.value)}
                            placeholder="10:30 AM"
                            className="h-8 w-28 text-xs"
                            data-testid={`ed-time-end-${day}-${i}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeTime(day, i)}
                            data-testid={`ed-time-remove-${day}-${i}`}
                            title="Remove this time"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => addTime(day)}
                        className="h-7 text-xs px-2 mt-1 border-dashed"
                        data-testid={`ed-add-time-${day}`}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add time
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-[10px] text-gray-500">
              Active days: <span className="font-mono">{activeDays().length ? activeDays().join(", ") : "(none yet)"}</span>
            </div>
          </div>

          {/* ── Suggest Best Times helper ───────────────────────────── */}
          <div className="mt-3 border-2 border-dashed border-[#D4A017]/40 rounded-lg p-3 bg-[#FFFBEB]">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div>
                <div className="text-sm font-bold text-[#1E3A8A]">Suggest Best Times</div>
                <div className="text-[11px] text-gray-600">
                  Avoids protected activities &amp; double-booked single-occupancy rooms.
                  Uses {activeDays().length > 0 ? activeDays().join(", ") : "all weekdays"} · {inferDurationMin()} min.
                </div>
              </div>
              <Button
                size="sm"
                onClick={onSuggest}
                disabled={suggesting || !r.camper_name.trim()}
                className="bg-[#D4A017] hover:bg-[#b88c12] text-white"
                data-testid="ed-suggest-times-btn"
              >
                {suggesting ? "Searching…" : "Suggest Best Times"}
              </Button>
            </div>
            {suggestions && suggestions.suggestions && suggestions.suggestions.length > 0 && (
              <div className="space-y-1.5" data-testid="ed-suggestions-list">
                <div className="text-[10px] uppercase tracking-wide text-gray-500">
                  Click to add (group {suggestions.group_code || "?"}):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.suggestions.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="text-[11px] px-2 py-1.5 rounded-md bg-white border border-[#D4A017]/50 hover:bg-[#FEF3C7] hover:border-[#D4A017] transition-colors"
                      data-testid={`ed-suggestion-${i}`}
                      title={`${s.activity}${s.room_clear !== null ? (s.room_clear ? " · room free" : " · room busy") : ""}`}
                    >
                      <span className="font-semibold text-[#1E3A8A]">{s.day.slice(0, 3)}</span>{" "}
                      <span className="font-mono">{s.times_raw}</span>{" "}
                      <span className="text-gray-500">{s.activity}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {suggestions && (!suggestions.suggestions || suggestions.suggestions.length === 0) && (
              <div className="text-[11px] text-gray-500 italic">
                {suggestions.messages?.[0] || "No clean windows found this week."}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="ed-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[#1E3A8A] hover:bg-[#16306d] text-white"
            data-testid="ed-save"
          >
            {saving ? "Saving…" : (mode === "add" ? "Add Camper" : "Save Changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <Label className="text-xs text-gray-600">{label}</Label>
      <Input value={value} onChange={onChange} placeholder={placeholder} className="h-9 mt-1" data-testid={testId} />
    </div>
  );
}

function TextareaField({ label, value, onChange, placeholder, testId }) {
  return (
    <div className="sm:col-span-1">
      <Label className="text-xs text-gray-600">{label}</Label>
      <Textarea value={value} onChange={onChange} placeholder={placeholder} className="mt-1 min-h-[64px] font-mono text-xs" data-testid={testId} />
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  const accents = {
    navy: "border-[#1E3A8A]/30 text-[#1E3A8A]",
    gold: "border-[#D4A017]/40 text-[#D4A017]",
    red: "border-red-300 text-red-700",
    amber: "border-amber-300 text-amber-700",
    muted: "border-gray-300 text-gray-700",
  };
  return (
    <Card className={`border-2 ${accents[accent] || accents.muted}`} data-testid={testId}>
      <CardContent className="py-3 px-4">
        <div className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</div>
        <div className="text-2xl font-heading font-bold mt-0.5">{value}</div>
      </CardContent>
    </Card>
  );
}

function AlertsPanel({ alerts, q, setQ }) {
  const filtered = alerts.filter(a => {
    if (!q.trim()) return true;
    const lc = q.trim().toLowerCase();
    return [a.camper_name, a.group_code, a.service, a.provider, a.head_counselor]
      .join(" ").toLowerCase().includes(lc);
  });

  const byDay = DAY_ORDER.reduce((acc, d) => {
    acc[d] = filtered.filter(a => a.day === d);
    return acc;
  }, {});

  const totalDeliverable = filtered.filter(a => a.deliverable).length;
  const totalBlocked = filtered.length - totalDeliverable;

  return (
    <Card data-testid="services-alerts-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base text-[#1E3A8A] flex items-center gap-2">
              <BellRing className="h-4 w-4 text-[#D4A017]" />
              Per-Session SMS Alerts
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Head counselors are auto-texted <span className="font-bold">10 minutes before</span> each
              session, every week — no general digest. Idempotent: one SMS per session per day.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> {totalDeliverable} scheduled
            </span>
            {totalBlocked > 0 && (
              <span className="flex items-center gap-1 text-red-700" title="Head counselor phone not on file">
                <PhoneOff className="h-3.5 w-3.5" /> {totalBlocked} missing HC phone
              </span>
            )}
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search"
                className="pl-8 h-9 w-48"
                data-testid="alerts-search"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[60vh]">
          <div className="divide-y">
            {DAY_ORDER.map(day => byDay[day].length > 0 && (
              <div key={day} className="p-3" data-testid={`alerts-day-${day.toLowerCase()}`}>
                <div className="font-heading font-bold text-sm text-[#1E3A8A] mb-2">{day}</div>
                <div className="space-y-1.5">
                  {byDay[day].map((a, i) => (
                    <div
                      key={`${day}-${i}`}
                      className={`flex items-start gap-3 px-3 py-2 rounded-lg border ${
                        a.deliverable ? "bg-white border-gray-200" : "bg-red-50 border-red-200"
                      }`}
                      data-testid={`alert-row-${day.toLowerCase()}-${i}`}
                    >
                      <div className="flex-shrink-0 text-center pt-0.5">
                        <Clock className="h-4 w-4 text-[#D4A017] mx-auto" />
                        <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                          -10 min
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-bold text-[#1E3A8A] text-sm">{a.camper_name}</span>
                          <Badge variant="outline" className="text-[10px] font-mono py-0">{a.group_code || "?"}</Badge>
                          <span className="text-xs text-gray-700">{a.service}{a.provider ? ` · ${a.provider}` : ""}</span>
                          {a.protected && (
                            <Badge className="bg-red-600 hover:bg-red-700 text-[10px]">
                              <AlertTriangle className="h-3 w-3 mr-0.5" /> Protected
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          Session: <span className="font-mono">{a.time_label}</span>
                          {a.scheduled_activity ? <> · pulling from <span className="font-medium">{a.scheduled_activity}</span></> : null}
                        </div>
                        <div className="text-[11px] mt-1">
                          {a.deliverable ? (
                            <span className="text-emerald-700">
                              → <span className="font-medium">{a.head_counselor || "HC"}</span>{" "}
                              <span className="font-mono text-gray-600">{a.phone}</span>
                            </span>
                          ) : (
                            <span className="text-red-700">
                              <PhoneOff className="h-3 w-3 inline mr-0.5" />
                              No HC phone on file for {a.group_code} — alert will not send
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {!filtered.length && (
              <div className="p-8 text-center text-gray-400 text-sm">No alerts scheduled.</div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
